import { useCallback, useEffect, useRef, useState } from 'react';
import { enhancedApi as billingV2EnhancedApi } from '@store/generated/billing-v2';
import { useAppDispatch } from '@store/hooks';
import type {
  InvoiceResponse,
  ReconcileInvoiceResponse,
  SendInvoiceReminderResponse,
  SendPaymentRequestRequest,
  SendPaymentRequestResponse,
} from '@store/generated/payments';
import { useSnackbar } from 'notistack';

// Pull the mutation from the generated billing-v2 API directly to avoid a
// payments → billing → payments cycle (the billing barrel re-exports
// components that import from @features/payments).
const { usePostV2ProviderChargeLinesPreviewMutation } = billingV2EnhancedApi;

import {
  useConfirmPatientPaymentMethodMutation,
  useCreateStripeConnectMutation,
  useCreateStripeDashboardLinkMutation,
  useDeletePatientPaymentMethodMutation,
  useGetPatientInvoicesQuery,
  useGetPatientPaymentMethodsQuery,
  useGetStripeConnectStatusQuery,
  useReconcileInvoiceMutation,
  useSendInvoiceReminderMutation,
  useSendPaymentRequestMutation,
  useSetDefaultPatientPaymentMethodMutation,
  useSetupPatientPaymentMethodMutation,
} from './api';

/**
 * Hook for managing Stripe Connect integration
 * Provides access to Stripe Connect status and onboarding
 */
export function useStripeConnect() {
   
  const { data: status, isLoading, error, refetch } = useGetStripeConnectStatusQuery();
  const [createStripeConnect, { isLoading: isCreating }] = useCreateStripeConnectMutation();

  const startOnboarding = useCallback(
    async (customReturnUrl?: string) => {
      try {
        // Use provided returnUrl or fallback to current URL
        const returnUrl = customReturnUrl || window.location.href;

        const result = await createStripeConnect({
          createStripeConnectRequest: {
            return_url: returnUrl,
            refresh_url: returnUrl,
          },
        }).unwrap();
        // The API returns an onboarding URL that the user should be redirected to
        return result;
      } catch (error) {
        console.error('Failed to start Stripe Connect onboarding:', error);
        throw error;
      }
    },
    [createStripeConnect],
  );

  return {
    status,
    isLoading,
    error,
    isCreating,
    startOnboarding,
    refetchStatus: refetch,
    // Computed properties
    hasStripeAccount: status?.has_account ?? false,
    isStripeEnabled: status?.charges_enabled && status.payouts_enabled,
    stripeAccountId: status?.stripe_account_id ?? null,
  };
}

/**
 * Hook for accessing Stripe Dashboard
 * Generates a link to the Stripe Express Dashboard
 */
export function useStripeDashboard() {
  const [createDashboardLink, { isLoading }] = useCreateStripeDashboardLinkMutation();

  const openDashboard = useCallback(async () => {
    try {
      const result = await createDashboardLink({
        createDashboardLinkRequest: {},
      }).unwrap();

      // Open the dashboard in a new tab
      window.open(result.dashboard_url, '_blank');
      return result;
    } catch (error) {
      console.error('Failed to create Stripe dashboard link:', error);
      throw error;
    }
  }, [createDashboardLink]);

  return {
    openDashboard,
    isLoading,
  };
}

/**
 * Hook for sending payment requests to patients
 * Allows providers to request payments via SMS
 */
export function useSendPaymentRequest() {
  const [sendPaymentRequest, result] = useSendPaymentRequestMutation();

  const mutate = useCallback(
    async (
      request: SendPaymentRequestRequest,
      options?: { onSuccess?: (data: SendPaymentRequestResponse) => void; onError?: () => void },
    ) => {
      try {
        const data = await sendPaymentRequest({
          sendPaymentRequestRequest: request,
        }).unwrap();
        options?.onSuccess?.(data);
        return data;
      } catch (error) {
        options?.onError?.();
        throw new Error(getPaymentRequestErrorMessage(error));
      }
    },
    [sendPaymentRequest],
  );

  return {
    mutate,
    ...result,
  };
}

function getPaymentRequestErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;

  if (typeof error === 'object' && error !== null) {
    const maybeApiError = error as {
      data?: { message?: unknown; error?: unknown; error_code?: unknown };
      message?: unknown;
      error?: unknown;
    };
    if (typeof maybeApiError.data?.message === 'string') {
      return maybeApiError.data.message;
    }
    if (typeof maybeApiError.data?.error === 'string') {
      return maybeApiError.data.error;
    }
    if (typeof maybeApiError.message === 'string') {
      return maybeApiError.message;
    }
    if (typeof maybeApiError.error === 'string') {
      return maybeApiError.error;
    }
  }

  return 'Failed to send payment request';
}

/**
 * Hook for re-sending an existing Stripe invoice via Stripe's hosted-invoice
 * email. Distinct from useSendPaymentRequest, which creates a NEW invoice.
 *
 * Backend is at POST /v2/provider/invoices/{invoice_id}/remind. It bumps a
 * server-side reminder counter, refreshes last_reminded_at, and extends the
 * 7-day suppression window so the patient drops back out of the work queue.
 *
 * Errors:
 *   - 400 if the invoice is paid, void, or uncollectible (Stripe can't
 *     re-send a closed invoice). Surfaced as a toast.
 *   - 404 if the invoice ID doesn't exist locally. Surfaced as a toast.
 */
export function useSendInvoiceReminder() {
  const { enqueueSnackbar } = useSnackbar();
  const [sendReminderMutation, result] = useSendInvoiceReminderMutation();

  const sendReminder = useCallback(
    async (
      invoiceId: string,
      options?: {
        onSuccess?: (data: SendInvoiceReminderResponse) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      try {
        const data = await sendReminderMutation({ invoiceId }).unwrap();
        enqueueSnackbar(
          data.message || 'Reminder sent — Stripe will re-email the invoice.',
          { variant: 'success' },
        );
        options?.onSuccess?.(data);
        return data;
      } catch (error) {
        const message = extractReminderErrorMessage(error);
        enqueueSnackbar(message, { variant: 'error' });
        options?.onError?.(error);
        return undefined;
      }
    },
    [sendReminderMutation, enqueueSnackbar],
  );

  return {
    sendReminder,
    isLoading: result.isLoading,
    error: result.error,
  };
}

/**
 * Hook for correcting a stale active invoice. This calls the dedicated
 * provider action that voids the existing invoice, records the reconciliation
 * decision, and can issue a replacement invoice tied to confirmed charge lines.
 */
export function useReconcileInvoice() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useAppDispatch();
  const [reconcileMutation, result] = useReconcileInvoiceMutation();

  const reconcileInvoice = useCallback(
    async (
      invoiceId: string,
      options?: {
        action?: 'void_invoice' | 'suspend_invoice_wait_secondary';
        note?: string | null;
        sendNotification?: boolean;
        onSuccess?: (data: ReconcileInvoiceResponse) => void;
        onError?: (error: unknown) => void;
      },
    ) => {
      try {
        const data = await reconcileMutation({
          invoiceId,
          reconcileInvoiceRequest: {
            action: options?.action ?? 'void_invoice',
            note: options?.note ?? null,
            send_notification: options?.sendNotification ?? true,
          },
        }).unwrap();
        dispatch(billingV2EnhancedApi.util.resetApiState());
        enqueueSnackbar(data.message || 'Invoice reconciled', { variant: 'success' });
        options?.onSuccess?.(data);
        return data;
      } catch (error) {
        enqueueSnackbar(extractReminderErrorMessage(error), { variant: 'error' });
        options?.onError?.(error);
        return undefined;
      }
    },
    [dispatch, enqueueSnackbar, reconcileMutation],
  );

  return {
    reconcileInvoice,
    isLoading: result.isLoading,
    error: result.error,
  };
}

/**
 * Hook for fetching a server-built receipt preview for a set of charge_line_ids.
 *
 * Backend at POST /v2/provider/charge-lines/preview enriches the bare IDs we
 * carry on PatientQueueItem with encounter context (date, type, provider),
 * per-line CPT/description/amount, and a synthesized `suggested_reason` the
 * modal pre-fills into the `reason` form field. Server validates that all
 * IDs belong to one patient (400 on mixed) and one organization (404 on
 * cross-org leakage).
 *
 * Auto-fires whenever `chargeLineIds` changes to a non-empty array. Pass
 * `undefined` or an empty array to skip.
 */
export function useChargeLinesPreview(chargeLineIds: string[] | undefined) {
  const [preview, { data, isLoading, error, isUninitialized }] =
    usePostV2ProviderChargeLinesPreviewMutation();

  // Re-fire when the ID set changes. Sort + join to make the dep stable
  // across reorderings of the same set.
  const idsKey = chargeLineIds && chargeLineIds.length > 0
    ? [...chargeLineIds].sort().join(',')
    : '';

  // Stash the trigger in a ref so it can't appear in the effect deps.
  // RTK Query's mutation `reset` (and sometimes `trigger`) gets a fresh
  // reference every render, so listing them in deps creates an infinite
  // loop: each call updates state → new ref → effect re-runs → new call.
  const previewRef = useRef(preview);
  previewRef.current = preview;
  const requestedIdsKeyRef = useRef('');

  useEffect(() => {
    if (!idsKey) return;
    if (requestedIdsKeyRef.current === idsKey && !isUninitialized) return;
    requestedIdsKeyRef.current = idsKey;
    void previewRef.current({
      chargeLinePreviewRequest: { charge_line_ids: idsKey.split(',') },
    });
  }, [idsKey, isUninitialized]);

  return {
    data,
    isLoading,
    error,
  };
}

function extractReminderErrorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const err = error as { status?: number; data?: { message?: string } };
    if (err.data?.message) return err.data.message;
    if (err.status === 404) return 'Invoice not found.';
    if (err.status === 400) {
      return "Can't send a reminder — this invoice is paid, void, or uncollectible.";
    }
  }
  return 'Failed to send reminder.';
}

/**
 * Hook for managing patient payment methods
 * Provides access to payment methods list and mutation actions
 *
 * Only fetches data when Stripe Connect is enabled for the provider
 */
export function usePatientPaymentMethods(patientId: string) {
  const { isStripeEnabled, isLoading: isStripeLoading } = useStripeConnect();

  // Only fetch payment methods if Stripe is enabled
  const {
    data: paymentMethodsData,
    isLoading: isLoadingMethods,
    error: methodsError,
    refetch: refetchMethods,
  } = useGetPatientPaymentMethodsQuery(
    { patientId },
    { skip: !isStripeEnabled || !patientId },
  );

  // Mutations
  const [setupPaymentMethod, { isLoading: isSettingUp }] = useSetupPatientPaymentMethodMutation();
  const [confirmPaymentMethod, { isLoading: isConfirming }] = useConfirmPatientPaymentMethodMutation();
  const [deletePaymentMethod, { isLoading: isDeleting }] = useDeletePatientPaymentMethodMutation();
  const [setDefaultPaymentMethod, { isLoading: isSettingDefault }] =
    useSetDefaultPatientPaymentMethodMutation();

  // Setup a new payment method - returns client_secret for Stripe Elements
  const setupNewPaymentMethod = useCallback(async () => {
    if (!patientId) throw new Error('Patient ID is required');

    const result = await setupPaymentMethod({ patientId }).unwrap();
    return result;
  }, [patientId, setupPaymentMethod]);

  // Confirm payment method after Stripe SetupIntent succeeds
  const confirmNewPaymentMethod = useCallback(async () => {
    if (!patientId) throw new Error('Patient ID is required');

    await confirmPaymentMethod({ patientId }).unwrap();
  }, [patientId, confirmPaymentMethod]);

  // Delete a payment method
  const deleteMethod = useCallback(
    async (paymentMethodId: string) => {
      if (!patientId) throw new Error('Patient ID is required');

      await deletePaymentMethod({ patientId, paymentMethodId }).unwrap();
    },
    [patientId, deletePaymentMethod],
  );

  // Set a payment method as default
  const setDefault = useCallback(
    async (paymentMethodId: string) => {
      if (!patientId) throw new Error('Patient ID is required');

      await setDefaultPaymentMethod({ patientId, paymentMethodId }).unwrap();
    },
    [patientId, setDefaultPaymentMethod],
  );

  return {
    // Data
    paymentMethods: paymentMethodsData?.payment_methods ?? [],
    totalMethods: paymentMethodsData?.total ?? 0,

    // Stripe Connect status
    isStripeEnabled,

    // Loading states
    isLoading: isStripeLoading || isLoadingMethods,
    isSettingUp,
    isConfirming,
    isDeleting,
    isSettingDefault,
    isMutating: isSettingUp || isConfirming || isDeleting || isSettingDefault,

    // Error
    error: methodsError,

    // Actions
    setupNewPaymentMethod,
    confirmNewPaymentMethod,
    deleteMethod,
    setDefault,
    refetch: refetchMethods,
  };
}

const DEFAULT_INVOICES_LIMIT = 5;

export type InvoiceStatusFilter = 'all' | 'paid' | 'open';

/**
 * Hook for managing patient invoices
 * Provides access to patient invoices with offset-based pagination
 *
 * Only fetches data when Stripe Connect is enabled for the provider
 */
export function usePatientInvoices(
  patientId: string,
  options?: { skip?: boolean; status?: InvoiceStatusFilter },
) {
  const { isStripeEnabled, isLoading: isStripeLoading } = useStripeConnect();

  // Track all loaded invoices and offset-based pagination
  const [allInvoices, setAllInvoices] = useState<InvoiceResponse[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Convert filter to API param (null means no filter)
  const statusParam = options?.status && options.status !== 'all' ? options.status : null;

  // Track which query's data we've already processed to avoid duplicates
  const processedQueryRef = useRef<string | undefined>(undefined);
  const getQueryKey = (o: number, s: string | null) => `${o}:${s ?? 'all'}`;

  // Determine if we should skip the query
  const shouldSkip = options?.skip || !isStripeEnabled || !patientId;

  // Query for initial data or next page
  const {
    data: invoicesData,
    isLoading: isLoadingInvoices,
    isFetching,
    error: invoicesError,
    refetch: refetchInvoices,
  } = useGetPatientInvoicesQuery(
    {
      patientId,
      limit: DEFAULT_INVOICES_LIMIT,
      offset,
      status: statusParam,
    },
    { skip: shouldSkip },
  );

  // Reset when patientId or status filter changes
  useEffect(() => {
    setAllInvoices([]);
    setOffset(0);
    setHasMore(false);
    processedQueryRef.current = undefined;
  }, [patientId, statusParam]);

  // Update local state when data arrives
  useEffect(() => {
    const newInvoices = invoicesData?.invoices;
    if (!newInvoices) return;

    // Skip if we're still fetching - data might be stale from cache
    if (isFetching) return;

    // Create a unique key for this query (offset + status)
    const queryKey = getQueryKey(offset, statusParam);

    // Only process if we haven't already processed this query's data
    if (processedQueryRef.current === queryKey) return;

    if (offset === 0) {
      // Initial load or refresh - replace all
      setAllInvoices(newInvoices);
    } else {
      // Load more - append new invoices
      setAllInvoices(prev => [...prev, ...newInvoices]);
    }

    // If we got fewer results than the limit, there are no more pages
    setHasMore(newInvoices.length >= DEFAULT_INVOICES_LIMIT);
    processedQueryRef.current = queryKey;
  }, [invoicesData, offset, statusParam, isFetching]);

  // Load more invoices
  const loadMore = useCallback(() => {
    if (hasMore && !isFetching) {
      setOffset(prev => prev + DEFAULT_INVOICES_LIMIT);
    }
  }, [hasMore, isFetching]);

  // Refresh the list (reset pagination)
  const refresh = useCallback(() => {
    setAllInvoices([]);
    setOffset(0);
    setHasMore(false);
    processedQueryRef.current = undefined;
    refetchInvoices();
  }, [refetchInvoices]);

  // Show loading when: initial load, or fetching with empty list (filter change)
  const isInitialOrFilterLoading = isLoadingInvoices || (isFetching && allInvoices.length === 0);

  return {
    // Data
    invoices: allInvoices,

    // Stripe Connect status
    isStripeEnabled,

    // Loading states
    isLoading: isStripeLoading || isInitialOrFilterLoading,
    isLoadingMore: isFetching && offset > 0 && allInvoices.length > 0,

    // Pagination
    hasMore,

    // Error
    error: invoicesError,

    // Actions
    loadMore,
    refetch: refresh,
  };
}
